import React from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import {
  WORKFLOW_END_ROUTE,
  WORKFLOW_END_ROUTE_LABEL,
  WorkflowCanvas,
  WorkflowPauseConfig,
} from "./workflowBuilderData";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";

interface WorkflowPausePanelProps {
  config: WorkflowPauseConfig;
  canvases: WorkflowCanvas[];
  currentCanvasId: string;
  onChange: (config: WorkflowPauseConfig) => void;
}

const WorkflowPausePanel: React.FC<WorkflowPausePanelProps> = ({
  config,
  canvases,
  currentCanvasId,
  onChange,
}) => {
  const viewOnly = useWorkflowReadOnly();
  const routeOptions = canvases.filter((canvas) => canvas.id !== currentCanvasId);

  return (
    <Box className="workflow-canvas-dialog__config-panel">
      <Alert severity="info">
        Pause holds the case for a set time, then moves on automatically. The canvas owner can
        resume it early.
      </Alert>
      <Box className="workflow-canvas-dialog__config-grid">
        <TextField
          disabled={viewOnly}
          label="Wait"
          type="number"
          size="small"
          value={config.amount}
          inputProps={{ min: 1 }}
          onChange={(event) =>
            onChange({ ...config, amount: Math.max(1, Number(event.target.value) || 1) })
          }
        />
        <FormControl disabled={viewOnly} size="small" fullWidth>
          <InputLabel id="pause-unit-label">Unit</InputLabel>
          <Select
            labelId="pause-unit-label"
            label="Unit"
            value={config.unit}
            onChange={(event: SelectChangeEvent) =>
              onChange({
                ...config,
                unit: event.target.value as WorkflowPauseConfig["unit"],
              })
            }
          >
            {/* Only shown for a loaded pause whose unit wasn't recognised; Validate flags it. */}
            {config.unit === "" && (
              <MenuItem value="" disabled className="workflow-canvas-dialog__placeholder-option">
                Choose a unit
              </MenuItem>
            )}
            <MenuItem value="hours">Hours</MenuItem>
            <MenuItem value="days">Days</MenuItem>
            <MenuItem value="working-days">Working days</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <FormControl disabled={viewOnly} size="small" fullWidth>
        <InputLabel id="pause-route-label">Then go to</InputLabel>
        <Select
          labelId="pause-route-label"
          label="Then go to"
          value={config.routeId}
          onChange={(event: SelectChangeEvent) =>
            onChange({ ...config, routeId: event.target.value })
          }
        >
          <MenuItem value="" className="workflow-canvas-dialog__placeholder-option">
            Continue to next canvas
          </MenuItem>
          {routeOptions.map((canvas) => (
            <MenuItem key={canvas.id} value={canvas.id}>
              {canvas.name}
            </MenuItem>
          ))}
          <MenuItem value={WORKFLOW_END_ROUTE}>{WORKFLOW_END_ROUTE_LABEL}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default WorkflowPausePanel;
